import { createContext, useContext, type ReactNode } from "react";
import { ScanBarcode, Truck } from "lucide-react";
import { useTranslation } from "../../i18n";
import { ScoreDisplay, type CycleScoreApi } from "../gamification";
import type { DispatchStats } from "./dispatchSession";

/** The packer's running session — today's counts and the trophy-points
 *  cycle — shared by every dispatch screen's bars.
 *
 *  These used to live in one floating card pinned bottom-right
 *  (dispatch-stats-bar.tsx). On the pack screen the box bar covered it
 *  completely, and the card was pointer-events: none, so the points pill
 *  never opened the trophies anywhere on dispatch. Each screen now places
 *  the pieces in its own bars: the points pill beside the title (as on
 *  every other scanning screen) and the counts in the bottom bar.
 *
 *  Both pieces render nothing without a provider, so the components stay
 *  usable on routes that don't track a session. */
type DispatchSessionValue = { stats: DispatchStats; cycle?: CycleScoreApi };

const DispatchSessionContext = createContext<DispatchSessionValue | null>(null);

export const DispatchSessionProvider = ({
    stats,
    cycle,
    children,
}: DispatchSessionValue & { children: ReactNode }) => (
    <DispatchSessionContext.Provider value={{ stats, cycle }}>
        {children}
    </DispatchSessionContext.Provider>
);

/** "86 scanned | 11 shipped" — for a screen's bottom bar. */
export const DispatchSessionStats = () => {
    const { t } = useTranslation();
    const session = useContext(DispatchSessionContext);
    if (!session) return null;
    return (
        <div className="dispatch-session-stats" data-testid="dispatch-session-stats">
            <span className="dispatch-session-stats-item">
                <ScanBarcode size={16} aria-hidden="true" />
                {session.stats.itemsScanned} {t("dispatch_scanned")}
            </span>
            <span className="dispatch-session-stats-divider" aria-hidden="true" />
            <span className="dispatch-session-stats-item">
                <Truck size={16} aria-hidden="true" />
                {session.stats.ordersShipped} {t("dispatch_shipped")}
            </span>
        </div>
    );
};

/** The trophy-points pill, only on a warehouse with a dispatch target. */
export const DispatchPointsPill = () => {
    const session = useContext(DispatchSessionContext);
    if (!session?.cycle?.scoring) return null;
    return <ScoreDisplay total={session.cycle.total} />;
};
